import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  saveDocument,
  uploadDocumentFile,
  checkMySign,
  getFormReceiverList,
  getFormExecutorList,
  getReferenceDocList,
  getDocumentReceiverList,
  getDocumentExecutorList,
  getDocumentReferenceDocList,
  getDocumentFileList,
  getDocumentCommentList,
  getDocumentContentImage,
} from '@entities/document/api/document';

/**
 * 문서 저장/상신 Mutation 훅
 * - PC 버전과 동일한 로직 적용
 * - 네트워크 오류 시 자동 재시도 (최대 2회)
 * @param {Object} options - React Query mutation 옵션
 * @returns {UseMutationResult} mutation 결과
 */
function useSaveDocumentMutation(options = {}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn(documentData) {
      return saveDocument(documentData);
    },
    onSuccess: (data, variables, context) => {
      // 문서 목록 캐시 무효화
      queryClient.invalidateQueries({ queryKey: ['approvalList'] });
      queryClient.invalidateQueries({ queryKey: ['pcApprovalList'] });

      if (options.onSuccess) {
        options.onSuccess(data, variables, context);
      }
    },
    onError: (error, variables, context) => {
      if (options.onError) {
        options.onError(error, variables, context);
      }
    },
  });
}

/**
 * 문서 첨부파일 업로드 Mutation 훅
 * - PC 버전과 동일한 로직 적용
 * @param {Object} options - React Query mutation 옵션
 * @returns {UseMutationResult} mutation 결과
 */
function useUploadDocumentFileMutation(options = {}) {
  return useMutation({
    mutationFn({ docIdx, file }) {
      return uploadDocumentFile(docIdx, file);
    },
    ...options,
  });
}

/**
 * 사용자 서명 조회 Query 훅
 * - 상신 전 서명 존재 여부 확인용
 * @param {Object} options - React Query 옵션
 * @returns {UseQueryResult} query 결과
 */
function useCheckMySignQuery(options = {}) {
  return useQuery({
    queryKey: ['mySign'],
    queryFn: checkMySign,
    staleTime: 1000 * 60 * 5, // 5분 캐싱
    ...options,
  });
}

/**
 * 양식별 기본 수신자 목록 조회 Query 훅
 * @param {number} docType - 문서 양식 코드
 * @param {number} companyCode - 회사 코드
 * @param {Object} options - React Query 옵션
 * @returns {UseQueryResult} query 결과
 */
function useFormReceiverListQuery(docType, companyCode, options = {}) {
  return useQuery({
    queryKey: ['formReceiverList', docType, companyCode],
    queryFn: () => getFormReceiverList(docType, companyCode),
    enabled: !!docType && !!companyCode,
    select: (data) => data?.resultData?.receiverUserList || [],
    ...options,
  });
}

/**
 * 양식별 기본 시행자 목록 조회 Query 훅
 * @param {number} docType - 문서 양식 코드
 * @param {number} companyCode - 회사 코드
 * @param {Object} options - React Query 옵션
 * @returns {UseQueryResult} query 결과
 */
function useFormExecutorListQuery(docType, companyCode, options = {}) {
  return useQuery({
    queryKey: ['formExecutorList', docType, companyCode],
    queryFn: () => getFormExecutorList(docType, companyCode),
    enabled: !!docType && !!companyCode,
    select: (data) => data?.resultData?.formExecutorList || [],
    ...options,
  });
}

/**
 * 참조 가능 문서 목록 조회 Query 훅 (참조문서 선택용)
 * @param {Object} params - 검색 조건
 * @param {Object} options - React Query 옵션
 * @returns {UseQueryResult} query 결과
 */
function useReferenceDocListQuery(params = {}, options = {}) {
  return useQuery({
    queryKey: ['referenceDocList', params],
    queryFn: () => getReferenceDocList(params),
    select: (data) => data?.resultData?.docInfoList || [],
    ...options,
  });
}

/**
 * 문서 수신자 목록 조회 Query 훅
 * @param {number} docIdx - 문서 인덱스
 * @param {Object} options - React Query 옵션
 * @returns {UseQueryResult} query 결과
 */
function useDocumentReceiverListQuery(docIdx, options = {}) {
  return useQuery({
    queryKey: ['documentReceiverList', docIdx],
    queryFn: () => getDocumentReceiverList(docIdx),
    enabled: !!docIdx,
    select: (data) => data?.resultData?.docReceiverList || data?.resultData || [],
    ...options,
  });
}

/**
 * 문서 시행자 목록 조회 Query 훅
 * @param {number} docIdx - 문서 인덱스
 * @param {Object} options - React Query 옵션
 * @returns {UseQueryResult} query 결과
 */
function useDocumentExecutorListQuery(docIdx, options = {}) {
  return useQuery({
    queryKey: ['documentExecutorList', docIdx],
    queryFn: () => getDocumentExecutorList(docIdx),
    enabled: !!docIdx,
    select: (data) => {
      // 여러 가능한 필드명 확인 (docExecutorList, executorList, formExecutorList 등)
      if (data?.resultData?.docExecutorList) {
        return data.resultData.docExecutorList;
      }
      if (data?.resultData?.executorList) {
        return data.resultData.executorList;
      }
      if (data?.resultData?.formExecutorList) {
        return data.resultData.formExecutorList;
      }
      // resultData가 직접 배열인 경우
      if (Array.isArray(data?.resultData)) {
        return data.resultData;
      }
      return [];
    },
    ...options,
  });
}

/**
 * 문서 참조문서 목록 조회 Query 훅
 * @param {number} docIdx - 문서 인덱스
 * @param {Object} options - React Query 옵션
 * @returns {UseQueryResult} query 결과
 */
function useDocumentReferenceDocsQuery(docIdx, options = {}) {
  return useQuery({
    queryKey: ['documentReferenceDocs', docIdx],
    queryFn: () => getDocumentReferenceDocList(docIdx),
    enabled: !!docIdx,
    select: (data) => data?.resultData?.referenceDocInfoList || data?.resultData || [],
    ...options,
  });
}

/**
 * 문서 첨부파일 목록 조회 Query 훅
 * @param {number} docIdx - 문서 인덱스
 * @param {Object} options - React Query 옵션
 * @returns {UseQueryResult} query 결과
 */
function useDocumentFileListQuery(docIdx, options = {}) {
  return useQuery({
    queryKey: ['documentFileList', docIdx],
    queryFn: () => getDocumentFileList(docIdx),
    enabled: !!docIdx,
    select: (data) => data?.resultData?.docFileInfoList || data?.resultData || [],
    ...options,
  });
}

/**
 * 문서 코멘트 목록 조회 Query 훅
 * @param {number} docIdx - 문서 인덱스
 * @param {Object} options - React Query 옵션
 * @returns {UseQueryResult} query 결과
 */
function useDocumentCommentListQuery(docIdx, options = {}) {
  return useQuery({
    queryKey: ['documentCommentList', docIdx],
    queryFn: () => getDocumentCommentList(docIdx),
    enabled: !!docIdx,
    select: (data) => data?.resultData?.commentInfoList || data?.resultData || [],
    ...options,
  });
}

/**
 * 문서 본문 이미지 조회 Query 훅
 * - 모바일 화면에서 문서 본문을 이미지로 표시할 때 사용
 * - Base64 인코딩된 이미지 데이터 반환 (파일 생성 없음, 모바일 최적화)
 * @param {number} docIdx - 문서 인덱스
 * @param {boolean} enabled - 쿼리 활성화 여부
 * @param {Object} options - React Query 옵션
 * @returns {UseQueryResult} query 결과 (base64Image, fileName, fileSize)
 */
function useDocumentContentImageQuery(docIdx, enabled = true, options = {}) {
  return useQuery({
    queryKey: ['documentContentImage', docIdx],
    queryFn: () => getDocumentContentImage(docIdx),
    enabled: !!docIdx && enabled,
    select: (data) => data?.resultData || null,
    staleTime: 1000 * 60 * 10, // 10분 캐싱 (이미지는 변경이 적음)
    ...options,
  });
}

export {
  useSaveDocumentMutation,
  useUploadDocumentFileMutation,
  useCheckMySignQuery,
  useFormReceiverListQuery,
  useFormExecutorListQuery,
  useReferenceDocListQuery,
  useDocumentReceiverListQuery,
  useDocumentExecutorListQuery,
  useDocumentReferenceDocsQuery,
  useDocumentFileListQuery,
  useDocumentCommentListQuery,
  useDocumentContentImageQuery,
};
